import * as THREE from 'three';
import { AbilitySystem } from '../../services/abilities/AbilitySystem';
import { AbilityMode, getAbilityList } from '../../services/hud/AbilityHUD';
import * as AbilityHUD from '../../services/hud/AbilityHUD';
import { HudManager } from '../../services/hud/HUDManager';
import * as AppStateManager from '../AppStateManager';
import { global } from '../GlobalState';
import { ControlsManager } from './ControlsManager';
import { Player } from './Player';
import { playerManager } from './PlayerManager';
import { PlayerControls } from './PlayerControls';
import { PlayerView } from './PlayerView';
import { defaultControlSchemeIds, defaultControlSchemes } from './ControlSchemes';
import { PointerLockFPS } from '../../services/controls/schemes/PointerLockFPS';
import { TouchLookFPS } from '../../services/controls/schemes/TouchLookFPS';
import { NoopScheme } from '../../services/controls/schemes/NoopScheme';
import SceneManager from '../SceneManager';
import { OrbFactory } from '../../orbs/OrbFactory';
export class PlayerSystem {
    constructor(config) {
        var _a;
        this.worldControlSchemes = new Map();
        this.sceneManager = SceneManager.getInstance();
        this.domParent = config.domParent;
        this.isMobile = config.isMobile;
        this.controlSchemes = Object.assign(Object.assign({}, defaultControlSchemes), ((_a = config.controlSchemes) !== null && _a !== void 0 ? _a : {}));
        this.initialControlSchemeId = config.initialControlSchemeId;
    }
    createLocalPlayer(config) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        const camera = new THREE.PerspectiveCamera((_a = config.fov) !== null && _a !== void 0 ? _a : 70, window.innerWidth / window.innerHeight, (_b = config.near) !== null && _b !== void 0 ? _b : 1, (_c = config.far) !== null && _c !== void 0 ? _c : 20000);
        camera.position.copy((_d = config.startPosition) !== null && _d !== void 0 ? _d : new THREE.Vector3(0, 1, 4));
        camera.rotation.order = 'YXZ';
        const startScene = (_e = config.startScene) !== null && _e !== void 0 ? _e : this.sceneManager.getCurrentSceneName();
        const view = new PlayerView({
            scene: this.sceneManager.getScene(startScene),
            camera,
            antialias: (_g = (_f = config.renderer) === null || _f === void 0 ? void 0 : _f.antialias) !== null && _g !== void 0 ? _g : true,
            powerPreference: (_j = (_h = config.renderer) === null || _h === void 0 ? void 0 : _h.powerPreference) !== null && _j !== void 0 ? _j : 'high-performance',
            logarithmicDepthBuffer: (_l = (_k = config.renderer) === null || _k === void 0 ? void 0 : _k.logarithmicDepthBuffer) !== null && _l !== void 0 ? _l : true,
        });
        view.mount(this.domParent);
        const controls = new PlayerControls(camera, view.renderer.domElement, { isMobile: this.isMobile });
        const hudManager = new HudManager({
            customFPS: true,
            objectCount: true,
            orbCount: true,
            systemUA: true,
        });
        const abilitySystem = new AbilitySystem();
        const raycaster = new THREE.Raycaster();
        const playerRef = { player: null };
        const context = {
            playerRef,
            controls,
            abilitySystem,
            hudManager,
            outlinePass: view.outlinePass,
            chat: global.chat,
        };
        const actionHandlers = {
            handleKeyboardPrimary: this.handleKeyboardPrimary,
            handleWheelAbility: this.handleWheelAbility,
            handleAbilityClick: this.handleAbilityClick,
            handleAbilityTouch: this.handleAbilityTouch,
            handleInspectAuxClick: this.handleInspectAuxClick,
            handleAbilityMouseDown: this.handleAbilityMouseDown,
            handleAbilityMouseUp: this.handleAbilityMouseUp,
        };
        const defaultSchemeId = (_m = this.initialControlSchemeId) !== null && _m !== void 0 ? _m : (this.isMobile ? defaultControlSchemeIds.mobile : defaultControlSchemeIds.desktop);
        const controlsManager = new ControlsManager({
            dom: view.renderer.domElement,
            controls,
            schemes: this.controlSchemes,
            movementFactories: {
                pointerLock: () => new PointerLockFPS(view.renderer.domElement, {
                    shouldIgnoreKeyboard: () => global.chat.isOpenWindow(),
                }),
                touchLook: () => new TouchLookFPS(view.renderer.domElement),
                noop: () => new NoopScheme(),
            },
            actionHandlers,
            context,
            initialSchemeId: defaultSchemeId,
        });
        const player = new Player({
            id: config.id,
            fov: config.fov,
            near: config.near,
            far: config.far,
            startPosition: config.startPosition,
            startScene,
            camera,
        }, controlsManager, hudManager, controls);
        playerRef.player = player;
        playerManager.setLocal(player, { replace: true });
        global.camera = camera;
        global.controls = controls;
        global.raycaster = raycaster;
        global.renderer = view.renderer;
        global.composer = view.composer;
        global.renderPass = view.renderPass;
        global.outlinePass = view.outlinePass;
        const unsubscribeSceneChange = this.sceneManager.on('currentChanged', ({ sceneName }) => {
            var _a;
            const nextScheme = (_a = this.worldControlSchemes.get(sceneName)) !== null && _a !== void 0 ? _a : defaultSchemeId;
            controlsManager.setScheme(nextScheme);
        });
        const dispose = () => {
            unsubscribeSceneChange();
            controlsManager.dispose();
            controls.dispose();
            view.dispose();
        };
        const setControlScheme = (schemeId) => controlsManager.setScheme(schemeId);
        const setWorldControlScheme = (sceneName, schemeId) => {
            this.worldControlSchemes.set(sceneName, schemeId);
        };
        let cachedConfig = null;
        const applyWorldConfig = (worldConfig) => {
            cachedConfig = Object.assign({}, worldConfig);
            if (worldConfig.controlSchemeId) {
                controlsManager.setScheme(worldConfig.controlSchemeId);
            }
            if (typeof worldConfig.fov === 'number') {
                camera.fov = worldConfig.fov;
                camera.updateProjectionMatrix();
            }
            if (typeof worldConfig.movementSpeed === 'number') {
                player.movementSpeed = worldConfig.movementSpeed;
            }
            if (typeof worldConfig.movementDrag === 'number') {
                player.movementDrag = worldConfig.movementDrag;
            }
        };
        const clearWorldConfig = () => {
            var _a;
            if (!cachedConfig)
                return;
            controlsManager.setScheme(defaultSchemeId);
            if (typeof cachedConfig.fov === 'number') {
                camera.fov = (_a = config.fov) !== null && _a !== void 0 ? _a : 70;
                camera.updateProjectionMatrix();
            }
            player.movementSpeed = 16;
            player.movementDrag = 8;
            cachedConfig = null;
        };
        return {
            player,
            controls,
            controlsManager,
            view,
            raycaster,
            hudManager,
            abilitySystem,
            dispose,
            setControlScheme,
            setWorldControlScheme,
            applyWorldConfig,
            clearWorldConfig,
        };
    }
    createRemotePlayer(id, options = {}) {
        var _a;
        const hudManager = new HudManager({
            customFPS: false,
            objectCount: false,
            orbCount: false,
            systemUA: false,
        });
        const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 1, 20000);
        camera.position.copy((_a = options.startPosition) !== null && _a !== void 0 ? _a : new THREE.Vector3(0, 1, 4));
        camera.rotation.order = 'YXZ';
        const controls = new PlayerControls(camera, this.domParent, { isMobile: true });
        const controlsManager = new ControlsManager({
            dom: this.domParent,
            controls,
            schemes: {
                noop: {
                    id: 'noop',
                    movement: 'noop',
                    bindings: [],
                },
            },
            movementFactories: {
                noop: () => new NoopScheme(),
            },
            actionHandlers: {},
            context: { playerRef: { player: null }, controls, abilitySystem: new AbilitySystem(), hudManager, outlinePass: undefined, chat: global.chat },
            initialSchemeId: 'noop',
        });
        const player = new Player({
            id,
            startPosition: options.startPosition,
            startScene: options.startScene,
            camera,
        }, controlsManager, hudManager, controls);
        playerManager.add(player, { isLocal: false });
        return player;
    }
    resolveWorldControlScheme(sceneName) {
        var _a;
        return (_a = this.worldControlSchemes.get(sceneName)) !== null && _a !== void 0 ? _a : null;
    }
    ensureLocalClientOrb(sceneName) {
        var _a;
        const localPlayer = playerManager.local;
        if (!localPlayer)
            return;
        const targetScene = (_a = sceneName !== null && sceneName !== void 0 ? sceneName : localPlayer.currentSceneName) !== null && _a !== void 0 ? _a : this.sceneManager.getCurrentSceneName();
        let clientOrb = localPlayer.getOrb();
        if (!clientOrb) {
            clientOrb = OrbFactory.createOrbByType('CLIENT_ORB', undefined, {
                clientId: localPlayer.id,
                role: 'localClient',
            });
            if (!clientOrb) {
                console.warn('[PlayerSystem] Failed to create CLIENT_ORB for local player.');
                return;
            }
        }
        global.orbManager.loadOrb(clientOrb);
        clientOrb.setPosition(localPlayer.camera.position.clone());
        localPlayer.setOrb(clientOrb);
        global.orbManager.addOrbToScene(clientOrb, targetScene);
    }
    handleKeyboardPrimary(event, context) {
        const keyboardEvent = event;
        const code = keyboardEvent.code;
        const chat = context.chat;
        if (chat.isOpenWindow()) {
            if (code === 'Slash') {
                chat.inputText += '/';
                keyboardEvent.preventDefault();
                keyboardEvent.stopImmediatePropagation();
                return;
            }
            if (code === 'Backquote') {
                chat.close();
                keyboardEvent.preventDefault();
                keyboardEvent.stopImmediatePropagation();
                return;
            }
            keyboardEvent.stopImmediatePropagation();
            return;
        }
        if (code === 'Slash' || code === 'KeyT') {
            chat.open();
            chat.inputText = code === 'Slash' ? '/' : '';
            keyboardEvent.preventDefault();
            keyboardEvent.stopImmediatePropagation();
            return;
        }
        if (/Digit[1-9]/.test(code)) {
            const number = parseInt(code.replace('Digit', ''), 10) - 1;
            const numAbilities = getAbilityList().length;
            if (number < numAbilities) {
                global.clickMode = number;
                AbilityHUD.updateAbilityBarHighlight();
                const tooltext = document.getElementById('tooltext');
                if (tooltext) {
                    tooltext.textContent = `${AbilityMode[global.clickMode]} (${global.clickMode})`;
                }
            }
        }
        if (code === 'KeyC') {
            const player = context.playerRef.player;
            if (player) {
                player.setHyperspeed(!player.hyperspeed);
            }
        }
        if (code === 'KeyP') {
            AppStateManager.toggleRendererPixelMode();
        }
    }
    handleWheelAbility(event, context) {
        var _a, _b;
        const wheelEvent = event;
        if (!context.controls.isLocked)
            return;
        if ((_b = (_a = context.chat).isOpenWindow) === null || _b === void 0 ? void 0 : _b.call(_a))
            return;
        if (global.isDragging) {
            wheelEvent.preventDefault();
            const delta = -Math.sign(wheelEvent.deltaY);
            const factor = 1.35;
            if (delta > 0) {
                global.dragDistance *= factor;
                global.dragDistance = Math.min(global.dragDistance, 10000);
            }
            else {
                global.dragDistance /= factor;
                global.dragDistance = Math.max(global.dragDistance, 0.5);
            }
            return;
        }
        const delta = Math.sign(wheelEvent.deltaY);
        const numAbilities = getAbilityList().length;
        if (delta === -1 && global.clickMode === 0) {
            global.clickMode = numAbilities;
        }
        global.clickMode = ((global.clickMode + delta) % numAbilities);
        if (global.clickMode < 0)
            global.clickMode = (numAbilities - 1);
        const tooltext = document.getElementById('tooltext');
        if (tooltext) {
            tooltext.textContent = `${AbilityMode[global.clickMode]} (${global.clickMode})`;
        }
        AbilityHUD.updateAbilityBarHighlight();
        if (context.outlinePass) {
            context.outlinePass.selectedObjects = [];
        }
    }
    handleAbilityClick(_, context) {
        if (!context.controls.isLocked)
            return;
        context.abilitySystem.handleAbility(global.clickMode);
    }
    handleAbilityTouch(event, context) {
        if (!context.controls.isLocked)
            return;
        const touchEvent = event;
        if (touchEvent.touches.length !== 1)
            return;
        const t = touchEvent.touches[0];
        context.abilitySystem.handleAbility(global.clickMode, { x: t.clientX, y: t.clientY });
    }
    handleInspectAuxClick(event, context) {
        if (!context.controls.isLocked)
            return;
        const mouseEvent = event;
        if (mouseEvent.button !== 1)
            return;
        mouseEvent.preventDefault();
        context.abilitySystem.handleAbility(AbilityMode.InspectObject, { x: mouseEvent.clientX, y: mouseEvent.clientY });
    }
    handleAbilityMouseDown(_, context) {
        if (!context.controls.isLocked)
            return;
        context.abilitySystem.handleMouseDown(global.clickMode);
    }
    handleAbilityMouseUp(_, context) {
        if (!context.controls.isLocked)
            return;
        context.abilitySystem.handleMouseUp(global.clickMode);
    }
}
